import React, { useState, useEffect } from 'react';
import { Select, InputNumber, Button, Space, Tooltip } from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
} from '@ant-design/icons';

const FONTS = [
  { label: 'Inter', value: 'Inter' },
  { label: 'Outfit', value: 'Outfit' },
  { label: 'Poppins', value: 'Poppins' },
  { label: 'Montserrat', value: 'Montserrat' },
  { label: 'Roboto', value: 'Roboto' },
];

export default function TextControls({ selectedObject, onStyleChange }) {
  const [fontFamily, setFontFamily] = useState('Outfit');
  const [fontSize, setFontSize] = useState(40);
  const [fill, setFill] = useState('#000000');
  const [textBackgroundColor, setTextBackgroundColor] = useState('');
  const [fontWeight, setFontWeight] = useState('normal');
  const [fontStyle, setFontStyle] = useState('normal');
  const [underline, setUnderline] = useState(false);
  const [textAlign, setTextAlign] = useState('center');

  // Sync state with selected object properties
  useEffect(() => {
    if (!selectedObject) return;

    setFontFamily(selectedObject.get('fontFamily') || 'Outfit');
    setFontSize(selectedObject.get('fontSize') || 40);
    setFill(selectedObject.get('fill') || '#000000');
    setTextBackgroundColor(selectedObject.get('textBackgroundColor') || '');
    setFontWeight(selectedObject.get('fontWeight') || 'normal');
    setFontStyle(selectedObject.get('fontStyle') || 'normal');
    setUnderline(selectedObject.get('underline') || false);
    setTextAlign(selectedObject.get('textAlign') || 'center');
  }, [selectedObject]);

  if (!selectedObject) {
    return (
      <div style={{ padding: '8px', color: '#bfbfbf', textAlign: 'center', fontStyle: 'italic' }}>
        Select a text item on the canvas to style it
      </div>
    );
  }

  const updateProp = (key, value) => {
    if (selectedObject.isEditing) {
      // If currently editing text, apply style only to the highlighted selection
      selectedObject.setSelectionStyles({ [key]: value });
    } else {
      // If object is just selected (not editing), apply to the whole object
      selectedObject.set(key, value);
      
      // We MUST also clear any per-character inline styles for this property, 
      // otherwise Fabric.js will keep prioritizing the old inline styles over the new global property!
      if (selectedObject.styles) {
        for (let line in selectedObject.styles) {
          for (let char in selectedObject.styles[line]) {
            if (selectedObject.styles[line][char] && selectedObject.styles[line][char][key] !== undefined) {
              delete selectedObject.styles[line][char][key];
            }
          }
        }
      }
    }
    selectedObject.dirty = true;
    onStyleChange();
  };

  const toggleBold = () => {
    const newValue = fontWeight === 'bold' ? 'normal' : 'bold';
    setFontWeight(newValue);
    updateProp('fontWeight', newValue);
  };

  const toggleItalic = () => {
    const newValue = fontStyle === 'italic' ? 'normal' : 'italic';
    setFontStyle(newValue);
    updateProp('fontStyle', newValue);
  };

  const toggleUnderline = () => {
    const newValue = !underline;
    setUnderline(newValue);
    updateProp('underline', newValue);
  };

  return (
    <div className="editor-right-panel" style={{ background: '#fff', border: 'none', padding: 0 }}>
      <div className="editor-section-title" style={{ marginBottom: '6px', fontSize: '12px' }}>Typography</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', alignItems: 'flex-end' }}>
        {/* Font Family */}
        <div style={{ flex: '1 1 180px', minWidth: '150px' }}>
          <label style={{ fontSize: '11px', color: '#8c8c8c', display: 'block', marginBottom: '2px' }}>Font Family</label>
          <Select
            value={fontFamily}
            onChange={(val) => {
              setFontFamily(val);
              updateProp('fontFamily', val);
            }}
            options={FONTS}
            style={{ width: '100%' }}
          />
        </div>

        {/* Font Size */}
        <div style={{ flex: '0 1 70px', minWidth: '60px' }}>
          <label style={{ fontSize: '11px', color: '#8c8c8c', display: 'block', marginBottom: '2px' }}>Size</label>
          <InputNumber
            min={8}
            max={200}
            value={fontSize}
            onChange={(val) => {
              if (val) {
                setFontSize(val);
                updateProp('fontSize', val);
              }
            }}
            style={{ width: '100%' }}
          />
        </div>

        {/* Color */}
        <div style={{ flex: '1 1 110px', minWidth: '95px' }}>
          <label style={{ fontSize: '11px', color: '#8c8c8c', display: 'block', marginBottom: '2px' }}>Color</label>
          <div className="color-picker-wrapper" style={{ height: '32px', display: 'flex', alignItems: 'center', padding: '4px 8px' }}>
            <input
              type="color"
              className="color-circle"
              value={fill}
              onChange={(e) => {
                const val = e.target.value;
                setFill(val);
                updateProp('fill', val);
              }}
              style={{ width: '20px', height: '20px' }}
            />
            <span style={{ fontSize: '11px', textTransform: 'uppercase', marginLeft: '6px' }}>{fill}</span>
          </div>
        </div>

        {/* Text Background (Highlight) */}
        <div style={{ flex: '1 1 130px', minWidth: '110px' }}>
          <label style={{ fontSize: '11px', color: '#8c8c8c', display: 'block', marginBottom: '2px' }}>Text Background</label>
          <div className="color-picker-wrapper" style={{ height: '32px', display: 'flex', alignItems: 'center', padding: '4px 8px' }}>
            <input
              type="color"
              className="color-circle"
              value={textBackgroundColor || '#ffffff'}
              onChange={(e) => {
                const val = e.target.value;
                setTextBackgroundColor(val);
                updateProp('textBackgroundColor', val);
              }}
              style={{ width: '20px', height: '20px' }}
            />
            <span style={{ fontSize: '11px', textTransform: 'uppercase', marginLeft: '6px' }}>
              {textBackgroundColor ? textBackgroundColor : 'None'}
            </span>
            {textBackgroundColor && (
              <Button
                type="link"
                size="small"
                danger
                onClick={() => {
                  setTextBackgroundColor('');
                  updateProp('textBackgroundColor', '');
                }}
                style={{ padding: 0, marginLeft: 'auto', fontSize: '10px', height: 'auto' }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Style Formatting (Bold, Italic, Underline) */}
        <div style={{ flex: '0 0 auto' }}>
          <label style={{ fontSize: '11px', color: '#8c8c8c', display: 'block', marginBottom: '2px' }}>Format</label>
          <Space size={4}>
            <Tooltip title="Bold">
              <Button
                type={fontWeight === 'bold' ? 'primary' : 'default'}
                icon={<BoldOutlined />}
                onClick={toggleBold}
              />
            </Tooltip>
            <Tooltip title="Italic">
              <Button
                type={fontStyle === 'italic' ? 'primary' : 'default'}
                icon={<ItalicOutlined />}
                onClick={toggleItalic}
              />
            </Tooltip>
            <Tooltip title="Underline">
              <Button
                type={underline ? 'primary' : 'default'}
                icon={<UnderlineOutlined />}
                onClick={toggleUnderline}
              />
            </Tooltip>
          </Space>
        </div>

        {/* Text Alignment */}
        <div style={{ flex: '0 0 auto' }}>
          <label style={{ fontSize: '11px', color: '#8c8c8c', display: 'block', marginBottom: '2px' }}>Alignment</label>
          <Space size={4}>
            <Tooltip title="Align Left">
              <Button
                type={textAlign === 'left' ? 'primary' : 'default'}
                icon={<AlignLeftOutlined />}
                onClick={() => {
                  setTextAlign('left');
                  updateProp('textAlign', 'left');
                }}
              />
            </Tooltip>
            <Tooltip title="Align Center">
              <Button
                type={textAlign === 'center' ? 'primary' : 'default'}
                icon={<AlignCenterOutlined />}
                onClick={() => {
                  setTextAlign('center');
                  updateProp('textAlign', 'center');
                }}
              />
            </Tooltip>
            <Tooltip title="Align Right">
              <Button
                type={textAlign === 'right' ? 'primary' : 'default'}
                icon={<AlignRightOutlined />}
                onClick={() => {
                  setTextAlign('right');
                  updateProp('textAlign', 'right');
                }}
              />
            </Tooltip>
          </Space>
        </div>
      </div>
    </div>
  );
}
